import { AbstractControl, ValidationErrors } from '@angular/forms'
import { IsoDate, md } from './dates'
import type { PaperKind } from './paper'
import { CopiedFrom, copyNote } from './form-copy'
import { PAPER_STATUS, PaperStatus, Tone } from './status'

export type EditorStatus = PaperStatus | 'new'

/** The spec's primary button per reviewed kind; the inspection forms keep the generic wording. JSAs have their own bar. */
const SUBMIT_LABEL: { readonly [K in Exclude<PaperKind, 'jsa'>]: string } = {
  dsr: 'Submit DSR →', form: 'Submit for Approval →', prf: 'Submit PRF →',
}

export type EditorBanner =
  | { readonly kind: 'lock'; readonly tone: 'submitted' | 'approved' | 'dnr'; readonly text: string; readonly reason: string | null }
  | { readonly kind: 'fix'; readonly reason: string }      // Rejected and editable: show why before the tech edits
  | { readonly kind: 'note'; readonly text: string }       // A Draft copied from another sheet: what carried over
  | null

export type BarActionId = 'save' | 'submit' | 'send' | 'delete' | 'duplicate' | 'done'
export interface BarAction {
  readonly id: BarActionId
  readonly label: string
  readonly look: 'outline' | 'primary' | 'text' | 'done'
  readonly ready: boolean
  /** Not tappable at all (the PRF's Submit while something is missing); `ready` alone only changes the look. */
  readonly disabled?: boolean
}
export interface BarState {
  readonly note: { readonly text: string; readonly tone: 'missing' | 'ready' | 'finish' | 'readonly' | 'sent' }
  readonly actions: readonly BarAction[]     // empty when read-only
}

export interface PaperEditorChrome {
  readonly pill: { readonly label: string; readonly tone: Tone }
  readonly readOnly: boolean
  readonly banner: EditorBanner
  readonly bar: BarState
}

function banner(status: EditorStatus, submittedOn: IsoDate | null, reviewer: string | null, reason: string | null, copied: CopiedFrom): EditorBanner {
  switch (status) {
    case 'submitted': return { kind: 'lock', tone: 'submitted', text: `Submitted${submittedOn ? ` ${md(submittedOn)}` : ''} — waiting on manager approval`, reason: null }
    case 'approved': return { kind: 'lock', tone: 'approved', text: reviewer ? `Approved by ${reviewer} — read only` : 'Approved — read only', reason: null }
    case 'dnr': return { kind: 'lock', tone: 'dnr', text: 'Rejected · Do not resubmit — read only', reason }
    case 'rejected': return reason ? { kind: 'fix', reason } : null
    default: { const text = copyNote(copied); return text ? { kind: 'note', text } : null }
  }
}

/** Sent items keep the mock-up's footer: "✓ Sent for approval" and a greyed "Submitted ✓" that does nothing. */
function lockedBar(status: EditorStatus): BarState {
  switch (status) {
    case 'submitted': return { note: { text: '✓ Sent for approval', tone: 'sent' }, actions: [{ id: 'done', label: 'Submitted ✓', look: 'done', ready: false }] }
    case 'approved': return { note: { text: '✓ Approved', tone: 'ready' }, actions: [{ id: 'done', label: 'Approved ✓', look: 'done', ready: false }] }
    default: return { note: { text: 'Read only', tone: 'readonly' }, actions: [] }
  }
}

/**
 * Status pill, lock or reason banner and sticky bar for a paper editor.
 * Read-only statuses get no actions. Rejected gets "Resubmit →" in place of the kind's submit label; that is the
 * only Resubmit in the app. Submit stays tappable when not ready: `ready` only changes its look, and tapping it
 * reveals the missing fields.
 */
export function paperEditor(input: {
  readonly kind: keyof typeof SUBMIT_LABEL
  readonly status: EditorStatus
  readonly submittedOn: IsoDate | null
  readonly reviewer: string | null
  readonly reason: string | null
  readonly copied?: CopiedFrom
  readonly missing: readonly string[]
}): PaperEditorChrome {
  const { kind, status, missing } = input
  const def = status === 'new' ? null : PAPER_STATUS[status]
  const readOnly = def?.readOnly ?? false
  const ready = missing.length === 0
  return {
    pill: def ? { label: kind === 'prf' && status === 'inProgress' ? 'Draft Saved' : def.label, tone: def.tone } : { label: 'New', tone: 'muted' },
    readOnly,
    // The DSR sheet carries no status of its own; it keeps only the rejection reason the tech has to fix.
    banner: kind === 'dsr' && status !== 'rejected' ? null : banner(status, input.submittedOn, input.reviewer, input.reason, input.copied ?? null),
    bar: readOnly
      ? lockedBar(status)
      : {
          note: ready ? { text: '✓ Ready to submit', tone: 'ready' } : { text: `${missing.length} required left`, tone: 'missing' },
          actions: [
            { id: 'save', label: 'Save Draft', look: 'outline', ready: true },
            { id: 'submit', label: status === 'rejected' ? 'Resubmit →' : SUBMIT_LABEL[kind], look: 'primary', ready },
          ],
        },
  }
}

/** The one wording for a control's errors: the inline message under the field and the chip in the requirements panel. */
export function controlMessage(errors: ValidationErrors | null | undefined): string {
  if (!errors) return ''
  // A validator that supplies its own wording wins — TIR's exhibit notes set { error: 'Required if a photo is attached.' }.
  if (typeof errors['error'] === 'string') return errors['error']
  if (errors['required']) return 'Required'
  if (errors['pattern']) return 'Invalid characters'
  if (errors['invalidDate']) return 'Invalid date'
  return 'Invalid'
}

/** Chip text for an invalid control: its label alone when it is empty, "Label — invalid characters" when it is filled in wrong. */
export function missingChip(label: string, errors: ValidationErrors | null | undefined): string {
  const message = controlMessage(errors)
  if (!message || message === 'Required') return label
  return `${label} — ${message.charAt(0).toLowerCase()}${message.slice(1)}`
}

/** Chips for the invalid controls, in `labels` order (DSR "Weather Conditions", "Start / end time — David", …). */
export function missingLabels(form: AbstractControl, labels: ReadonlyArray<readonly [path: string, label: string]>): string[] {
  return labels.map(([path, label]) => [form.get(path), label] as const).filter(([c]) => c?.invalid).map(([c, label]) => missingChip(label, c?.errors))
}
